// Engineer PWA Fault Assistant (Session AFC, extended Session AFD). Ask a plain-language question
// about a fault and get an answer drawn from the knowledge base (manuals, bulletins, previous
// reports) plus this portfolio's own call-out history, with cited sources. Three entry points feed
// it: a call-out job sheet (?jobId), a lift record (?assetId), and the home screen (no context).
//
// Live-only by design: the answer needs a server round-trip to the AI, so the Ask button is gated on
// connectivity and is NEVER queued through the offline outbox (unlike a job sheet). Everything else
// on the PWA stays offline-first; this one screen is honest about needing a signal.
//
// Session AFD added four things the engineer needs before trusting an answer at 375px, one-handed:
// inline [N] citation markers that jump to the passage, expandable source cards showing the exact
// retrieved text and how strongly it matched, an amber notice when the answer came from the open web
// rather than ELE's own records, and an inline prompt for the manufacturer and model when retrieval
// was too weak to answer at all.
import { Fragment, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { goBackOrHome, useRoutedOverlay } from "../../lib/useRoutedOverlay";
import {
  FAULT_CATEGORIES,
  WEB_SEARCH_NOTICE,
  WEB_SEARCH_NO_SOURCES_NOTICE,
  docTypeLabel,
  matchStrength,
  matchStrengthLabel,
  matchStrengthPill,
  knowledgePageImageUrl,
  pagePlaceLabel,
  parseAnswerSegments,
  tierLabel,
  tierPill,
  type FaultCategory,
  type KnowledgeEngineerQueryResponse,
  type KnowledgeLiftContext,
  type KnowledgePageSearchResponse,
  type KnowledgePageSearchResult,
  type KnowledgeQueryContext,
  type KnowledgeQuerySource,
  type KnowledgeWebSource,
  type PillVariant,
} from "../../lib/knowledgeTypes";
import { KnowledgePageViewer } from "../../components/KnowledgePageViewer";
import { EngineerDocumentViewer } from "./EngineerDocumentViewer";
import { useEngineerData } from "./EngineerDataContext";
import { PrintedRows, SearchNotes } from "./KnowledgeRows";

// Session BAV, "Find the page": the matched fault code is shown in bold wherever it appears in a result's snippet.
// Pure and exported so the splitting is tested on its own. Case-insensitive; the code is matched literally.
export function highlightParts(snippet: string, code: string | null): Array<{ text: string; match: boolean }> {
  if (!code || !code.trim()) return [{ text: snippet, match: false }];
  const escaped = code.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${escaped})`, "gi");
  return snippet
    .split(re)
    .filter((part) => part.length > 0)
    .map((part) => ({ text: part, match: part.toLowerCase() === code.trim().toLowerCase() }));
}

// Above this many characters the excerpt gets its own scroll box rather than pushing the rest of the
// card off a phone screen.
const LONG_EXCERPT_CHARS = 600;

// How long the amber flash on a cited card takes to fade back (CSS transition, no animation library).
const FLASH_MS = 600;

// Locally styled strength chips. The engineer shell is NOT inside .office-ui, where the shared
// .ele-pill sits at 10px and reads too small on a phone, so the same locked colours are applied here
// at 11px. Amber takes the locked tint #fbf3dd as its TEXT colour: #b8860b as text on the near-black
// #050816 ground is legible but muddy, and the tint is the colour already paired with it.
const PILL_CLASS: Record<PillVariant, string> = {
  green: "border-[#63C41F] bg-[rgba(99,196,31,0.15)] text-[var(--ele-green-ink)]",
  blue: "border-[#2CB9F3] bg-[rgba(44,185,243,0.15)] text-[var(--ele-loler-ink)]",
  amber: "border-[#b8860b] bg-[rgba(184,134,11,0.22)] text-[var(--ele-amber-ink)]",
  red: "border-[#c0392b] bg-[rgba(192,57,43,0.15)] text-[#c0392b]",
  purple: "border-[#7d21c7] bg-[rgba(125,33,199,0.18)] text-ele-strong",
  grey: "border-ele-purple-accent bg-ele-surface text-ele-body",
};

// A short date for the history rows (e.g. "15 Jun 2026").
function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// Names the equipment, never a specific site. The server figure is a distinct-SITE count (of other
// sites where documents about this equipment appear), so the wording says "sites", not "lifts".
function similarFaultsLine(count: number, manufacturer: string | null, model: string | null): string {
  const equip = [manufacturer, model].filter(Boolean).join(" ");
  const noun = count === 1 ? "site" : "sites";
  return equip
    ? `Documents on this ${equip} equipment are also on file for ${count} other ${noun} in your portfolio.`
    : `Related documents are also on file for ${count} other ${noun} in your portfolio.`;
}

// An engineer who has asked for reduced motion gets an instant jump instead of a smooth scroll.
// Guarded because matchMedia is not implemented in every test environment.
function scrollBehaviour(): ScrollBehavior {
  const reduce =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduce ? "auto" : "smooth";
}

export function FaultAssistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const jobId = params.get("jobId");
  const assetId = params.get("assetId");
  const { online } = useEngineerData();

  const [context, setContext] = useState<KnowledgeLiftContext | null>(null);
  const [contextLoaded, setContextLoaded] = useState(false);

  const [question, setQuestion] = useState("");
  const [category, setCategory] = useState<FaultCategory | null>(null);
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<KnowledgeEngineerQueryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Equipment the engineer typed in after a needs_context prompt. Kept for the rest of the session so
  // a follow-up question does not ask for the same manufacturer and model again.
  const [typedManufacturer, setTypedManufacturer] = useState("");
  const [typedModel, setTypedModel] = useState("");

  // Source-card interaction state, reset on every new answer.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [flash, setFlash] = useState<{ index: number; nonce: number } | null>(null);
  // Session BBX1: every step is a route (the URL's query string), so Back and the in-app arrow go one step back:
  // the results, the find list and each viewer are history entries, not component state.
  const viewerStep = useRoutedOverlay("v");
  const resultsStep = useRoutedOverlay("step");
  const viewerValue = viewerStep.value;
  const viewerIndex = viewerValue?.startsWith("src:") ? Number(viewerValue.slice(4)) : null;

  // Session BAV, "Find the page": a page search with no AI answer, and the page viewer it (and a source card with a
  // page) opens. The results stay in state while the viewer is open, so Back returns to the same list.
  const [findQuery, setFindQuery] = useState("");
  const [finding, setFinding] = useState(false);
  const [findResults, setFindResults] = useState<KnowledgePageSearchResult[] | null>(null);
  const [findError, setFindError] = useState<string | null>(null);
  const [findResponse, setFindResponse] = useState<KnowledgePageSearchResponse | null>(null);
  // Session BBX3: suggestions as you type, real headings, models and codes of the resolved maker (or a maker name), from
  // the equipment vocabulary only. Best effort and silent: a failed or offline lookup shows none.
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const pageViewer = (() => {
    const m = /^page:(.+):(\d+)$/.exec(viewerValue ?? "");
    return m ? { documentId: m[1]!, pageNumber: Number(m[2]) } : null;
  })();
  const setPageViewer = (v: { documentId: string; pageNumber: number } | null) =>
    v ? viewerStep.open(`page:${v.documentId}:${v.pageNumber}`) : viewerStep.close();
  const setViewerIndex = (i: number | null) => (i === null ? viewerStep.close() : viewerStep.open(`src:${i}`));
  // The list and the answer are shown only on their own step; Back from either returns to the form.
  const showResults = resultsStep.value === "results";
  const showFind = resultsStep.value === "find";

  useEffect(() => {
    const typed = findQuery.trim();
    if (typed.length < 2 || !online || typeof api.knowledge.suggestEngineer !== "function") {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.knowledge
        .suggestEngineer(typed, { ...(assetId ? { assetId } : {}), ...(typedManufacturer.trim() ? { maker: typedManufacturer.trim() } : {}) })
        .then((r) => {
          if (!cancelled) setSuggestions((r.suggestions ?? []).map((x) => x.text).slice(0, 8));
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [findQuery, online, assetId, typedManufacturer]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sourceRefs = useRef<Array<HTMLLIElement | null>>([]);

  const hasContextParam = Boolean(jobId || assetId);

  // Pre-fill the lift context when we have a param and a connection. A failure falls back to no
  // context (a free-text question still works); a job with no lift resolves to null. Fetched once.
  useEffect(() => {
    if (!online || contextLoaded || !hasContextParam) return;
    let active = true;
    const load = jobId ? api.knowledge.liftContextByJob(jobId) : api.knowledge.liftContext(assetId as string);
    load
      .then((res) => {
        if (active) setContext(res.context);
      })
      .catch(() => {
        // A missing or failed context must not block a free-text question.
      })
      .finally(() => {
        if (active) setContextLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [online, contextLoaded, hasContextParam, jobId, assetId]);

  // Clear the flash after it has faded. The timer is cleared on unmount and re-armed if a second
  // marker points at the same card (the nonce changes even when the index does not).
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), FLASH_MS);
    return () => clearTimeout(t);
  }, [flash]);

  const sources = answer?.sources ?? [];
  const webSources: KnowledgeWebSource[] = answer?.webSources ?? [];
  const needsContext = answer?.status === "needs_context";

  const canAsk = question.trim().length > 0 && !asking && online;
  const canSearchAgain =
    (typedManufacturer.trim().length > 0 || typedModel.trim().length > 0) && !asking && online;

  // Build the query context from present fields only. The schema expects a string or omission, so
  // a null or an empty string is never sent. Anything the engineer typed after a needs_context
  // prompt wins over the loaded lift context, because they are looking at the machine. Shared by Ask
  // and Find the page, so both search with the same lift.
  function buildQueryContext(): KnowledgeQueryContext {
    const queryContext: KnowledgeQueryContext = {};
    if (context) {
      if (context.siteId) queryContext.siteId = context.siteId;
      if (context.assetId) queryContext.assetId = context.assetId;
      if (context.manufacturer) queryContext.manufacturer = context.manufacturer;
    }
    if (typedManufacturer.trim()) queryContext.manufacturer = typedManufacturer.trim();
    if (typedModel.trim()) queryContext.liftModel = typedModel.trim();
    return queryContext;
  }

  const canFind = findQuery.trim().length > 0 && !finding && online;

  async function findPage(options?: { widen?: boolean }) {
    const trimmed = findQuery.trim();
    if (!trimmed || finding || !online) return;
    const queryContext = buildQueryContext();
    const hasContext = Object.keys(queryContext).length > 0;
    setFinding(true);
    setFindError(null);
    setFindResults(null);
    try {
      const res = await api.knowledge.searchPagesEngineer({
        question: trimmed,
        ...(hasContext ? { context: queryContext } : {}),
        ...(options?.widen ? { widen: true } : {}),
      });
      setFindResults(res.results);
      setFindResponse(res);
      if (resultsStep.value === "find") resultsStep.replace("find");
      else resultsStep.open("find");
    } catch (err) {
      setFindError(friendlyApiError(err, "The search could not run just now. Check your signal and try again."));
    } finally {
      setFinding(false);
    }
  }

  async function ask(options?: { keepAnswer?: boolean; widen?: boolean; maker?: string }) {
    const trimmed = question.trim();
    if (!trimmed || asking || !online) return;
    // The category is a visible word of the question, never a hidden prefix: a prefix of "Fault category: door." made
    // every bare number look like a fault code and polluted the search with words the engineer never typed.
    const composed = category ? `${trimmed} ${category}` : trimmed;

    const queryContext = buildQueryContext();
    // A maker picked from the picker travels as its own field, for THAT question only: it resolves a picker (two makers
    // named, or the lift's maker against the question's) and is never kept for the next question.
    const pickedMaker = options?.maker;
    const hasContext = Object.keys(queryContext).length > 0;

    setAsking(true);
    setError(null);
    // A needs_context re-ask keeps the prompt on screen so its own busy state is visible; a fresh Ask
    // clears the previous answer.
    if (!options?.keepAnswer) setAnswer(null);
    try {
      const res = await api.knowledge.queryEngineer({
        question: composed,
        includeV2History: true,
        ...(hasContext ? { context: queryContext } : {}),
        ...(options?.widen ? { widen: true } : {}),
        ...(pickedMaker ? { pickedMaker } : {}),
      });
      setAnswer(res);
      setExpanded({});
      setFlash(null);
      sourceRefs.current = [];
      if (resultsStep.value === "results") resultsStep.replace("results");
      else resultsStep.open("results");
    } catch (err) {
      setError(friendlyApiError(err, "The Fault Assistant could not answer just now. Please try again in a moment."));
    } finally {
      setAsking(false);
    }
  }

  function askAnother() {
    setAnswer(null);
    setError(null);
    setExpanded({});
    setFlash(null);
    if (showResults) resultsStep.close();
    // Keep the loaded context and its strip; just return focus for the next question.
    textareaRef.current?.focus();
  }

  // Tapping a [N] marker opens the card it points at, scrolls it into view and flashes it. Opening it
  // matters: landing on a collapsed row would tell the engineer nothing.
  function goToSource(index: number) {
    setExpanded((prev) => ({ ...prev, [index]: true }));
    setFlash({ index, nonce: Date.now() });
    requestAnimationFrame(() => {
      sourceRefs.current[index]?.scrollIntoView?.({ behavior: scrollBehaviour(), block: "center" });
    });
  }

  const contextPills = context
    ? [context.liftType, context.manufacturer, context.ageBand].filter((v): v is string => Boolean(v))
    : [];

  const viewerSource = viewerIndex != null ? sources[viewerIndex] : undefined;

  return (
    <>
      {/* `inert` while the read-only document viewer is open, with the viewer rendered OUTSIDE this
          container (the pattern OfficeLayout uses for its navigation drawer). The whole page behind the
          overlay leaves the tab order and the accessibility tree, so Tab stays inside the dialog
          instead of walking out into a page the reader cannot see, and the dialog's aria-modal="true"
          tells the truth. No hand-rolled focus trap. */}
      <div className="pb-8" inert={viewerSource || pageViewer ? true : undefined}>
        <button
          type="button"
          onClick={() => goBackOrHome(navigate, location.key)}
          className="mb-3 inline-flex min-h-11 min-w-11 items-center font-menu text-xs tracking-wider text-ele-green"
        >
          &larr; Back
        </button>

        <h1 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">Fault Assistant</h1>
        {context && (
          <p className="mb-3 text-sm text-ele-body">
            {(context.eleNumber ?? "Lift") + (context.siteName ? ` · ${context.siteName}` : "")}
          </p>
        )}

        {contextPills.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {contextPills.map((p) => (
              <span
                key={p}
                className="rounded border border-ele-purple-accent px-2.5 py-1 font-menu text-[11px] uppercase tracking-wide text-ele-body"
              >
                {p}
              </span>
            ))}
          </div>
        )}

        {hasContextParam && !contextLoaded && !online && (
          <p className="mb-3 text-sm text-ele-body opacity-70">
            The lift details will load when you are back online. You can still type your question.
          </p>
        )}

        {/* Session BAV: Find the page. A fault code or a symptom straight to the manual pages that cover it, with no
            AI answer in between. Live only, like Ask, and disabled offline; the existing offline notice below Ask covers both. */}
        <section className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void findPage();
            }}
          >
            <label htmlFor="find-page" className="mb-1 block font-menu text-xs uppercase tracking-wider text-ele-body">
              Find the page
            </label>
            <input
              id="find-page"
              type="search"
              value={findQuery}
              onChange={(e) => setFindQuery(e.target.value)}
              placeholder="Fault code or symptom, e.g. E37"
              enterKeyHint="search"
              list="find-page-suggestions"
              autoComplete="off"
              className="min-h-12 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-green"
            />
            <datalist id="find-page-suggestions">
              {suggestions.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <button
              type="submit"
              disabled={!canFind}
              aria-busy={finding || undefined}
              className="mt-3 min-h-12 w-full rounded-ele border border-ele-blue py-3 font-heading text-lg tracking-wide text-ele-blue transition hover:bg-ele-blue/10 disabled:opacity-50"
            >
              {finding ? "Searching..." : "Find the page"}
            </button>
          </form>
          {findError && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger p-3 text-sm text-ele-strong">
              {findError}
            </p>
          )}
          {showFind && findResults && (findResponse?.correction || findResponse?.alsoSearched) && (
            <SearchNotes correction={findResponse?.correction} alsoSearched={findResponse?.alsoSearched} />
          )}
          {showFind && findResults && findResults.length === 0 && (
            <div role="status" className="mt-3 text-sm text-ele-body">
              <p>{findResponse?.notFoundText ?? "No pages matched. Try the fault code exactly as shown on the controller, or describe the fault."}</p>
              {findResponse?.nearest && findResponse.nearest.length > 0 && (
                <ul className="mt-2 space-y-2" aria-label="Nearest sections">
                  {findResponse.nearest.map((n) => (
                    <li key={`${n.documentId}-${n.pageNumber}`}>
                      <button
                        type="button"
                        disabled={n.pageNumber == null}
                        onClick={() => n.pageNumber != null && setPageViewer({ documentId: n.documentId, pageNumber: n.pageNumber })}
                        className="min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-surface p-3 text-left text-ele-strong"
                      >
                        {(n.heading ?? n.fileName) + (n.pageNumber != null ? ` (page ${n.pageNumber})` : "")}
                        {n.maker ? ` - ${n.maker}` : ""}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {findResponse?.scope?.maker && !findResponse.widened && (
                <button
                  type="button"
                  onClick={() => void findPage({ widen: true })}
                  className="mt-3 min-h-11 w-full rounded-ele border border-ele-blue py-3 text-center font-heading text-sm tracking-wide text-ele-blue"
                >
                  Search all makers
                </button>
              )}
            </div>
          )}
          {showFind && findResults && findResults.length > 0 && (
            <ul className="mt-3 space-y-2" aria-label="Pages found">
              {findResults.map((r) => (
                <li key={r.chunkId}>
                  <PageResultCard
                    result={r}
                    onOpen={() => {
                      if (r.pageNumber != null) setPageViewer({ documentId: r.documentId, pageNumber: r.pageNumber });
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        <label htmlFor="fault-question" className="mb-1 block font-menu text-xs uppercase tracking-wider text-ele-body">
          Your question
        </label>
        <textarea
          id="fault-question"
          ref={textareaRef}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={4}
          placeholder="Describe the fault or symptom..."
          className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-ele-strong outline-none focus:border-ele-green"
        />

        <p className="mt-3 mb-2 font-menu text-xs uppercase tracking-wider text-ele-body">Fault category (optional)</p>
        <div className="flex flex-wrap gap-2">
          {FAULT_CATEGORIES.map((cat) => {
            const selected = category === cat;
            return (
              <button
                key={cat}
                type="button"
                aria-pressed={selected}
                onClick={() => setCategory(selected ? null : cat)}
                className={`min-h-11 rounded px-3 font-menu text-[11px] tracking-wide transition ${
                  selected
                    ? "border border-ele-green bg-ele-green/15 text-ele-green"
                    : "border border-ele-purple-accent text-ele-body hover:border-ele-green"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {!online && (
          <p className="mt-3 text-sm text-ele-body opacity-70">
            The Fault Assistant needs a connection. It will work when you are back online.
          </p>
        )}

        <button
          type="button"
          onClick={() => void ask()}
          disabled={!canAsk}
          className="mt-4 min-h-11 w-full rounded-ele bg-ele-green py-3 font-heading text-lg tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
        >
          {asking ? "Asking..." : "Ask"}
        </button>

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-ele border border-ele-danger bg-[rgba(255,90,90,0.1)] p-3 text-sm text-ele-strong"
          >
            {error}
          </p>
        )}

        {showResults && answer && (
          <div className="mt-6">
            {/* The answer came from the open web, not ELE's own records. A plain visible block, never
                inside a disclosure: the engineer must see it without opening anything. */}
            {answer.answerSource === "web_search" && (
              <p className="mb-4 rounded-ele border border-[#b8860b] bg-[#fbf3dd] p-3 text-sm text-ele-bg">
                {WEB_SEARCH_NOTICE}
              </p>
            )}

            <h2 className="mb-2 font-heading text-lg text-ele-blue">
              {needsContext ? "More detail needed" : answer.status === "not_found" ? "Not found" : "Answer"}
            </h2>

            <SearchNotes correction={answer.correction} alsoSearched={answer.alsoSearched} />
            <p className="mt-2 whitespace-pre-wrap text-sm text-ele-strong">
              {parseAnswerSegments(answer.answer, sources.length).map((seg, i) =>
                seg.kind === "text" ? (
                  <Fragment key={i}>{seg.text}</Fragment>
                ) : (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goToSource(seg.sourceIndex)}
                    aria-label={`Go to source ${seg.label}`}
                    // .ele-kb-cite (src/index.css, unscoped) extends the touch area to a usable size on
                    // a coarse pointer without changing the badge's visible size. The inline values
                    // below keep the engineer marker's own smaller footprint; the class supplies the
                    // positioning the hit-area overlay needs.
                    className="ele-kb-cite mx-0.5 inline-block cursor-pointer align-super font-menu leading-none"
                    style={{
                      backgroundColor: "#7d21c7",
                      color: "#FFFFFF",
                      borderRadius: "3px",
                      padding: "1px 4px",
                      fontSize: "10px",
                    }}
                  >
                    {seg.label}
                  </button>
                ),
              )}
            </p>

            {/* Retrieval was too weak and no equipment was named. Ask for it here, in place, rather than
                sending the engineer to another screen. What they type is kept for later questions too. */}
            {/* Session BBX1: Not found names the nearest sections and offers the ONE explicit widening. Nothing outside
                the maker is ever shown as a match; the chip below is the person's own tap. */}
            {answer.status === "not_found" && (
              <div className="mt-4">
                {answer.nearest && answer.nearest.length > 0 && (
                  <ul className="space-y-2" aria-label="Nearest sections">
                    {answer.nearest.map((n) => (
                      <li key={`${n.documentId}-${n.pageNumber}`}>
                        <button
                          type="button"
                          disabled={n.pageNumber == null}
                          onClick={() => n.pageNumber != null && setPageViewer({ documentId: n.documentId, pageNumber: n.pageNumber })}
                          className="min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-surface p-3 text-left text-ele-strong"
                        >
                          {(n.heading ?? n.fileName) + (n.pageNumber != null ? ` (page ${n.pageNumber})` : "")}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {answer.scope?.maker && !answer.widened && (
                  <button
                    type="button"
                    onClick={() => void ask({ keepAnswer: true, widen: true })}
                    disabled={asking || !online}
                    className="mt-3 min-h-11 w-full rounded-ele border border-ele-blue py-3 text-center font-heading text-sm tracking-wide text-ele-blue disabled:opacity-50"
                  >
                    Search all makers
                  </button>
                )}
              </div>
            )}

            {needsContext && (
              <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
                {answer.pickMaker && answer.pickMaker.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Pick the maker">
                    {answer.pickMaker.map((name) => (
                      <button
                        key={name}
                        type="button"
                        disabled={asking || !online}
                        onClick={() => {
                          void ask({ keepAnswer: true, maker: name });
                        }}
                        className="min-h-11 rounded border border-ele-green px-3 font-menu text-xs tracking-wide text-ele-green disabled:opacity-50"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
                <label
                  htmlFor="fault-manufacturer"
                  className="mb-1 block font-menu text-xs uppercase tracking-wider text-ele-body"
                >
                  Manufacturer
                </label>
                <input
                  id="fault-manufacturer"
                  value={typedManufacturer}
                  onChange={(e) => setTypedManufacturer(e.target.value)}
                  placeholder="For example: Kone"
                  className="min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-ele-strong outline-none focus:border-ele-green"
                />
                <label
                  htmlFor="fault-model"
                  className="mb-1 mt-3 block font-menu text-xs uppercase tracking-wider text-ele-body"
                >
                  Model
                </label>
                <input
                  id="fault-model"
                  value={typedModel}
                  onChange={(e) => setTypedModel(e.target.value)}
                  placeholder="For example: MonoSpace 500"
                  className="min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-ele-strong outline-none focus:border-ele-green"
                />
                <button
                  type="button"
                  onClick={() => void ask({ keepAnswer: true })}
                  disabled={!canSearchAgain}
                  className="mt-3 min-h-11 w-full rounded-ele bg-ele-green py-3 font-heading text-base tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
                >
                  {asking ? "Asking..." : "Search again"}
                </button>
              </div>
            )}

            {sources.length > 0 && (
              <section className="mt-5">
                <h2 className="mb-2 font-menu text-xs uppercase tracking-wider text-ele-body">
                  Sources ({sources.length})
                </h2>
                {/* One card per RETRIEVED PASSAGE, in the server's order and never deduped by document:
                    the [N] markers index this array, so collapsing two chunks of one manual into a
                    single row would make [2] point at the wrong card. */}
                <ul className="space-y-2">
                  {sources.map((s, i) => (
                    <SourceCard
                      key={`${s.documentId}-${s.chunkIndex}-${i}`}
                      source={s}
                      index={i}
                      expanded={Boolean(expanded[i])}
                      flashing={flash?.index === i}
                      onToggle={() => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))}
                      // Session BAV: a passage with a page opens that page (its picture when there is one) in the
                      // page viewer. One with no page, or from a server older than BAV (whose sources carry no
                      // hasPageImage at all, so there is no page route behind them), keeps the read-only passage
                      // viewer, as before.
                      onView={() =>
                        s.pageNumber != null && s.hasPageImage !== undefined
                          ? setPageViewer({ documentId: s.documentId, pageNumber: s.pageNumber })
                          : setViewerIndex(i)
                      }
                      registerRef={(el) => {
                        sourceRefs.current[i] = el;
                      }}
                    />
                  ))}
                </ul>
              </section>
            )}

            {/* A web answer can come back with NO citations at all: the model may not have run a
                search, or it searched and matched nothing. Left silent, the empty area reads as though
                the answer was sourced when it is really the model's own general knowledge. Said here in
                the same amber treatment, in place of the (absent) list. The main notice above stays. */}
            {answer.answerSource === "web_search" && webSources.length === 0 && (
              <p className="mt-5 rounded-ele border border-[#b8860b] bg-[#fbf3dd] p-3 text-sm text-ele-bg">
                {WEB_SEARCH_NO_SOURCES_NOTICE}
              </p>
            )}

            {/* Web results must be shown to the reader alongside the answer, so they are never tucked
                behind a disclosure the engineer might not open. */}
            {webSources.length > 0 && (
              <section className="mt-5">
                <h2 className="mb-2 font-menu text-xs uppercase tracking-wider text-ele-body">
                  Web sources ({webSources.length})
                </h2>
                <ul className="space-y-2">
                  {webSources.map((w, i) => (
                    <li key={`${w.url}-${i}`}>
                      <a
                        href={w.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block min-h-11 rounded-ele border border-ele-field-border bg-ele-surface p-3 transition hover:border-ele-blue"
                      >
                        <span className={`inline-flex rounded border px-2 py-0.5 font-menu text-[11px] uppercase tracking-wide ${PILL_CLASS.amber}`}>
                          Web source
                        </span>
                        <span className="mt-1.5 block break-words font-heading text-sm text-ele-strong">{w.title}</span>
                        <span className="mt-0.5 block break-words font-menu text-[11px] text-ele-blue">{w.domain}</span>
                        <span className="mt-1 block text-sm text-ele-body">{w.snippet}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Only on a knowledge-base answer. On a web answer the amber banner above has already
                explained why no ELE documents are listed, and repeating "no matching documents were
                found" underneath it would restate the very claim that banner was reworded to avoid:
                documents usually WERE retrieved, they were simply too weak to answer from. */}
            {!needsContext && answer.answerSource === "knowledge_base" && sources.length === 0 && (
              <p className="mt-5 text-sm text-ele-body">No matching documents were found.</p>
            )}

            {/* Only beside a KNOWLEDGE BASE answer. The count is derived from the retrieved chunks, and
                a web-search answer deliberately shows none of them as sources, so this line under a web
                answer would tell the engineer "also recorded at 3 other sites" beneath an answer taken
                off the internet with no ELE source cards at all to back it up. */}
            {context && answer.answerSource === "knowledge_base" && answer.similarSitesCount > 0 && (
              <p className="mt-4 text-sm text-ele-body">
                {similarFaultsLine(answer.similarSitesCount, context.manufacturer, context.model)}
              </p>
            )}

            {context && (
              <section className="mt-6">
                <h2 className="mb-2 font-menu text-xs uppercase tracking-wider text-ele-body">History on this lift</h2>
                {context.recentCallouts.length === 0 ? (
                  <p className="text-sm text-ele-body">No call-outs recorded on this lift.</p>
                ) : (
                  <ul className="space-y-2">
                    {context.recentCallouts.map((c, i) => (
                      <li
                        key={`${c.jobRef ?? "callout"}-${i}`}
                        className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3"
                      >
                        <p className="font-heading text-sm text-ele-strong">{shortDate(c.date)}</p>
                        <p className="mt-0.5 text-sm text-ele-body">{c.fault ?? "No fault description recorded"}</p>
                        <p className="mt-0.5 text-xs text-ele-body opacity-70">{c.outcome}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            <button
              type="button"
              onClick={askAnother}
              className="mt-6 min-h-11 w-full rounded-ele border border-ele-blue py-3 text-center font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
            >
              Ask another question
            </button>
          </div>
        )}
      </div>
      {viewerSource && <EngineerDocumentViewer source={viewerSource} onClose={() => setViewerIndex(null)} />}
      {pageViewer && (
        <KnowledgePageViewer
          documentId={pageViewer.documentId}
          pageNumber={pageViewer.pageNumber}
          onNavigate={(n) => viewerStep.replace(`page:${pageViewer.documentId}:${n}`)}
          onClose={() => setPageViewer(null)}
        />
      )}
    </>
  );
}

// One page found by Find the page: a large tap target that opens the page. A result with no page number (a file with
// no fixed pages) cannot open the viewer, so it offers the original instead, when there is one.
function PageResultCard({ result, onOpen }: { result: KnowledgePageSearchResult; onOpen: () => void }) {
  const place = pagePlaceLabel(result.pageNumber, result.pageLabel);
  const body = (
    <>
      <span className="flex flex-wrap items-center gap-2">
        {result.manufacturer && (
          <span className="font-menu text-[11px] uppercase tracking-wide text-ele-blue">{result.manufacturer}</span>
        )}
        {result.tier && (
          <span className={`inline-flex rounded border px-2 py-0.5 font-menu text-[11px] uppercase tracking-wide ${PILL_CLASS[tierPill(result.tier)]}`}>
            {tierLabel(result.tier)}
          </span>
        )}
        {result.safety && (
          <span className={`inline-flex rounded border px-2 py-0.5 font-menu text-[11px] uppercase tracking-wide ${PILL_CLASS.amber}`}>
            Safety procedure
          </span>
        )}
        {result.isDiagram && (
          <span className={`inline-flex rounded border px-2 py-0.5 font-menu text-[11px] uppercase tracking-wide ${PILL_CLASS.blue}`}>
            Diagram
          </span>
        )}
        {result.hasPageImage && (
          <span className={`inline-flex rounded border px-2 py-0.5 font-menu text-[11px] uppercase tracking-wide ${PILL_CLASS.grey}`}>
            Picture
          </span>
        )}
      </span>
      <span className="mt-1 block break-words font-heading text-base text-ele-strong">{result.fileName}</span>
      {place && <span className="mt-0.5 block font-menu text-xs tracking-wide text-ele-body">{place}</span>}
      {/* Session BBX4 (D-BBX3-4): a safety page shows its picture first, then its text, then the printed row. */}
      {result.safety && result.hasPageImage && result.pageNumber != null && (
        <img
          src={knowledgePageImageUrl(result.documentId, result.pageNumber)}
          alt={`Page ${result.pageNumber} of ${result.fileName}, as printed`}
          loading="lazy"
          data-testid="safety-page-image"
          className="mt-2 block w-full rounded-ele border border-ele-purple-accent bg-white"
        />
      )}
      <span className="mt-1 block break-words text-sm text-ele-body">
        {highlightParts(result.snippet, result.matchedCode).map((part, i) =>
          part.match ? (
            <strong key={i} className="font-bold text-ele-strong">
              {part.text}
            </strong>
          ) : (
            <Fragment key={i}>{part.text}</Fragment>
          ),
        )}
      </span>
      <PrintedRows rows={result.rows} />
    </>
  );
  const cardCls =
    "block min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-field p-3 text-left transition hover:border-ele-blue";
  if (result.pageNumber != null) {
    return (
      <button type="button" onClick={onOpen} className={cardCls}>
        {body}
      </button>
    );
  }
  if (result.openOriginalUrl) {
    return (
      <a href={result.openOriginalUrl} target="_blank" rel="noopener noreferrer" className={cardCls}>
        {body}
        <span className="mt-1 block font-menu text-xs text-ele-blue underline">Open original</span>
      </a>
    );
  }
  return <div className={cardCls}>{body}</div>;
}

// One retrieved passage. Collapsed it is a tap target showing what the document is and how well it
// matched; expanded it shows the exact text the assistant read, so the engineer can check the answer
// against the wording rather than taking it on trust.
function SourceCard({
  source,
  index,
  expanded,
  flashing,
  onToggle,
  onView,
  registerRef,
}: {
  source: KnowledgeQuerySource;
  index: number;
  expanded: boolean;
  flashing: boolean;
  onToggle: () => void;
  onView: () => void;
  registerRef: (el: HTMLLIElement | null) => void;
}) {
  const strength = matchStrength(source.similarityScore);
  const equipment = [source.manufacturer, source.liftModel].filter(Boolean).join(" · ");
  const longExcerpt = source.chunkText.length > LONG_EXCERPT_CHARS;

  return (
    <li
      ref={registerRef}
      // The flash is a background wash, never an edge accent (the no-left-stripe rule). It appears
      // instantly (transition off while flashing) and fades back over 600ms once the flag clears.
      className={`rounded-ele border border-ele-purple-accent ${flashing ? "bg-[rgba(184,134,11,0.35)]" : "bg-ele-surface"}`}
      style={{ transition: flashing ? "none" : `background-color ${FLASH_MS}ms ease` }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex min-h-11 w-full flex-col items-stretch rounded-ele p-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ele-purple"
      >
        <span className="flex items-center gap-2">
          {/* The card's own number, visible open or closed. The answer says "[2]" and the reader has
              to be able to find card 2 by eye, not only by tapping the marker. The office card
              already shows this; the engineer card did not. The hidden word makes the bare digit
              read as "Source 2" rather than a stray number to a screen reader. */}
          <span
            className={`flex-none rounded border px-2 py-0.5 font-menu text-[11px] leading-none tracking-wide ${PILL_CLASS.purple}`}
          >
            <span className="sr-only">Source </span>
            {index + 1}
          </span>
          <span className="min-w-0 flex-1 truncate font-heading text-sm text-ele-strong">{source.fileName}</span>
          <span
            aria-hidden
            className="flex-none font-menu text-sm text-ele-body transition-transform"
            style={{ transform: expanded ? "rotate(90deg)" : "none" }}
          >
            &#9656;
          </span>
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-2">
          <span className="font-menu text-[11px] uppercase tracking-wide text-ele-body">
            {docTypeLabel(source.docType)}
          </span>
          {/* Session BBX1: words, never a percentage. A source from an older server (no tier) keeps its old strength
              chip; a safety page says so first, because it is read as printed and never summarised. */}
          {source.safety && (
            <span className={`inline-flex rounded border px-2 py-0.5 font-menu text-[11px] uppercase tracking-wide ${PILL_CLASS.amber}`}>
              Safety procedure
            </span>
          )}
          <span
            className={`inline-flex rounded border px-2 py-0.5 font-menu text-[11px] uppercase tracking-wide ${
              PILL_CLASS[source.tier ? tierPill(source.tier) : matchStrengthPill(strength)]
            }`}
          >
            {source.tier ? tierLabel(source.tier) : matchStrengthLabel(strength)}
          </span>
          {/* A document with no page markers reports null. Render nothing at all for it: never "p.null". */}
          {source.pageNumber != null && (
            <span className="font-menu text-[11px] text-ele-body opacity-70">p.{source.pageNumber}</span>
          )}
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-3">
          {equipment && (
            <span className="mb-2 inline-flex rounded border border-ele-purple-accent px-2 py-0.5 font-menu text-[11px] uppercase tracking-wide text-ele-body">
              {equipment}
            </span>
          )}
          {/* Session BBX4 (D-BBX3-4): a SAFETY page is read as printed, in this order and no other: the page picture, then the
              page's own stored text, then the printed row. Nothing is summarised, combined or reworded, and a row is never the
              first thing a person sees on a safety step. Any other result keeps the order it always had. */}
          {source.safety && source.pageNumber != null && source.hasPageImage && (
            <figure className="mb-2" data-testid="safety-page-image">
              <img
                src={knowledgePageImageUrl(source.documentId, source.pageNumber)}
                alt={`Page ${source.pageNumber} of ${source.fileName}, as printed`}
                loading="lazy"
                className="w-full rounded-ele border border-ele-purple-accent bg-white"
              />
              <figcaption className="mt-1 font-menu text-[11px] uppercase tracking-wide text-ele-body">The page, as printed</figcaption>
            </figure>
          )}
          {!source.safety && <PrintedRows rows={source.rows} />}
          {source.safety && (
            <span className="mb-1 block font-menu text-[11px] uppercase tracking-wide text-ele-body" data-testid="safety-text-label">
              The page text, as stored
            </span>
          )}
          {/* border-left on the BLOCKQUOTE only, never on the card. The no-left-stripe rule bans
              DECORATIVE accent stripes on cards, tiles and nav items; a quotation rule is the one
              legitimate structural use of a left border and marks this as quoted source text, not
              the assistant's answer. The mono stack reinforces that. */}
          <blockquote
            className={`whitespace-pre-wrap break-words pl-3 font-mono text-[13px] leading-relaxed text-ele-body ${
              longExcerpt ? "max-h-[300px] overflow-y-auto" : ""
            }`}
            style={{ borderLeft: "3px solid #7d21c7" }}
          >
            {source.chunkText}
          </blockquote>
          {source.safety && <PrintedRows rows={source.rows} />}
          <button
            type="button"
            onClick={onView}
            className="mt-3 min-h-11 w-full rounded-ele border border-ele-blue py-2 text-center font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
          >
            View in document
          </button>
        </div>
      )}
    </li>
  );
}
